/**
 * Llamadas HTTP del dominio Inventario.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'`, el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request,
 * así que las operaciones de rollos van contra la **sede activa** sin tocar las
 * cabeceras a mano (multi-sede). El overview admin es global (no usa la sede).
 * Un `ApiError` (401/403/404/4xx/5xx) lo traduce el cliente y lo gestionan los
 * hooks/pantallas.
 *
 * Cubre la superficie de la API de dominio (`application/api/domains/
 * inventario.py`): rollos (listar/añadir/consumir/borrar) y administración por
 * sede (overview + toggle de inventario/láser).
 */
import { api } from "@/lib/api";

import type {
  AddRolloBody,
  AdminOverview,
  ConsumeBody,
  RollosData,
  ToggleResult,
} from "./types";

/** Ruta base del dominio (relativa al prefijo `/api/v1` que pone `lib/api`). */
const BASE = "inventario";

/**
 * Rollos de la sede activa (`GET /api/v1/inventario/rollos`).
 *
 * Permiso backend: `view_inventory`. Devuelve la galvanizada y el acero agrupados
 * más el historial de consumo (150 últimos) de la sede activa.
 */
export async function fetchRollos(): Promise<RollosData> {
  const response = await api.get(`${BASE}/rollos`);
  return (await response.json()) as RollosData;
}

/**
 * Añade rollos (galv por kg / acero por metros) a la sede activa
 * (`POST /api/v1/inventario/rollos`).
 *
 * Permiso backend: `manage_inventory`. La sede es la del request (no la del
 * cuerpo). Devuelve `{ok: true}` (201).
 */
export async function addRollo(body: AddRolloBody): Promise<{ ok: boolean }> {
  const response = await api.post(`${BASE}/rollos`, { json: body });
  return (await response.json()) as { ok: boolean };
}

/**
 * Consume cantidad de un rollo de la sede activa
 * (`POST /api/v1/inventario/consume`).
 *
 * Permiso backend: `manage_inventory`. Registra el consumo en el historial y
 * descuenta (borra el rollo si el resto llega a 0). El actor del JWT queda en el
 * historial. Devuelve `{ok: true}` (200).
 */
export async function consumeRollo(body: ConsumeBody): Promise<{ ok: boolean }> {
  const response = await api.post(`${BASE}/consume`, { json: body });
  return (await response.json()) as { ok: boolean };
}

/**
 * Elimina un rollo por id (`DELETE /api/v1/inventario/rollos/<id>`).
 *
 * Permiso backend: `manage_inventory`. 404 si el rollo no existe o es de otra
 * sede; 204 sin cuerpo si se elimina.
 */
export async function deleteRollo(rolloId: number): Promise<void> {
  await api.delete(`${BASE}/rollos/${rolloId}`);
}

/**
 * Overview de activación por sede (`GET /api/v1/inventario/admin`).
 *
 * Permiso backend: `all` (solo admin). Es una vista **global** (todas las sedes
 * activas), así que NO usa la sede activa; devuelve `{items: [...]}` con el estado
 * del módulo de inventario y del láser por sede.
 */
export async function fetchAdminOverview(): Promise<AdminOverview> {
  const response = await api.get(`${BASE}/admin`);
  return (await response.json()) as AdminOverview;
}

/**
 * Activa/desactiva el módulo de inventario de una sede
 * (`POST /api/v1/inventario/admin/toggle`).
 *
 * Permiso backend: `all`. La sede objetivo viaja por el header `X-Sede-Id`, que
 * `lib/api` inyecta desde el store de sede activa; el hook de mutación
 * (`useToggleInventario`) fija ese store a `sedeId` durante la petición. El actor
 * del JWT queda como `updated_by`. Devuelve `{sede_id, estado, activo}`. El
 * parámetro `sedeId` se conserva en la firma para que el hook lo pase explícito.
 */
export async function toggleInventario(_sedeId: number): Promise<ToggleResult> {
  const response = await api.post(`${BASE}/admin/toggle`);
  return (await response.json()) as ToggleResult;
}

/**
 * Activa/desactiva el horario láser de una sede
 * (`POST /api/v1/inventario/admin/laser/toggle`).
 *
 * Permiso backend: `all`. Misma forma que el toggle de inventario: la sede
 * objetivo va por `X-Sede-Id` (lo fija el hook de mutación en el store).
 * Devuelve `{sede_id, estado, activo}`.
 */
export async function toggleLaser(_sedeId: number): Promise<ToggleResult> {
  const response = await api.post(`${BASE}/admin/laser/toggle`);
  return (await response.json()) as ToggleResult;
}
